import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
} from '@angular/core';

const BORDER_BOX = 'border-box';
const DEFAULT_SHADOW_MAX_OPACITY = 0.6;

export type ContentShadowSide = 'top' | 'right' | 'bottom' | 'left';

const SIDES: readonly ContentShadowSide[] = ['top', 'right', 'bottom', 'left'];

/**
 * Each shadow is 20px across, placed and faded through the custom properties `measure()` sets on
 * the host: the container's visible area (`--cbs-x`, `--cbs-y`, `--cbs-w`, `--cbs-h`), the top
 * inset (`--cbs-top-inset`) and one opacity per side (`--cbs-opacity-<side>`). Until the first
 * measure they are unset and the fallbacks keep every shadow empty.
 */
const SIDE_CLASS: Record<ContentShadowSide, string> = {
  top:
    'tw:bg-linear-to-b tw:h-5 tw:left-[var(--cbs-x,0)] tw:top-[calc(var(--cbs-y,0px)+var(--cbs-top-inset,0px))] ' +
    'tw:w-[var(--cbs-w,0)] tw:opacity-[var(--cbs-opacity-top,0)]',
  right:
    'tw:bg-linear-to-l tw:w-5 tw:left-[calc(var(--cbs-x,0px)+var(--cbs-w,0px)-20px)] tw:top-[var(--cbs-y,0)] ' +
    'tw:h-[var(--cbs-h,0)] tw:opacity-[var(--cbs-opacity-right,0)]',
  bottom:
    'tw:bg-linear-to-t tw:h-5 tw:left-[var(--cbs-x,0)] tw:top-[calc(var(--cbs-y,0px)+var(--cbs-h,0px)-20px)] ' +
    'tw:w-[var(--cbs-w,0)] tw:opacity-[var(--cbs-opacity-bottom,0)]',
  left:
    'tw:bg-linear-to-r tw:w-5 tw:left-[var(--cbs-x,0)] tw:top-[var(--cbs-y,0)] ' +
    'tw:h-[var(--cbs-h,0)] tw:opacity-[var(--cbs-opacity-left,0)]',
};

/** Scroll distance from an edge at which that edge's shadow reaches full strength. */
const FULL_STRENGTH_DISTANCE = 100;

/** Leeway for the sub-pixel error of measured rects, so a shadow meant to end flush can't end past. */
const EDGE_LEEWAY = 0.1;

interface Size {
  width: number;
  height: number;
}

/**
 * The container's border box and its visible area — the padding box minus scrollbars — at their
 * fractional sizes. `offset*`/`client*` are snapped to whole pixels: a map at half of a 90vw dialog
 * is 773.55px wide but reports 774, and the right shadow sized to that sticks half a pixel out of
 * the container, enough for the nearest scrolling ancestor to show a horizontal scrollbar.
 */
function exactSizes(container: HTMLElement): { borderBox: Size; visible: Size } {
  const style = getComputedStyle(container);
  const px = (value: string) => parseFloat(value) || 0;
  const isBorderBox = style.boxSizing === BORDER_BOX;

  const exact = (
    cssSize: string,
    border: number,
    padding: number,
    offsetSize: number,
    clientSize: number,
  ) => {
    const size = parseFloat(cssSize);

    if (!Number.isFinite(size)) {
      return { borderBox: offsetSize, visible: clientSize };
    }

    // Both snapped sizes round the same box, so their difference is borders plus scrollbar.
    const scrollbar = Math.max(Math.round(offsetSize - clientSize - border), 0);

    // A content-box size already excludes the scrollbar; a border-box size includes it.
    if (!isBorderBox) {
      return { borderBox: size + padding + border + scrollbar, visible: size + padding };
    }

    return { borderBox: size, visible: Math.max(size - border - scrollbar, 0) };
  };

  const width = exact(
    style.width,
    px(style.borderLeftWidth) + px(style.borderRightWidth),
    px(style.paddingLeft) + px(style.paddingRight),
    container.offsetWidth,
    container.clientWidth,
  );
  const height = exact(
    style.height,
    px(style.borderTopWidth) + px(style.borderBottomWidth),
    px(style.paddingTop) + px(style.paddingBottom),
    container.offsetHeight,
    container.clientHeight,
  );

  return {
    borderBox: { width: width.borderBox, height: height.borderBox },
    visible: { width: width.visible, height: height.visible },
  };
}

/**
 * A shadow's length, rounded down to a device pixel with some leeway. One that ends even 0.03px
 * past the container gives it scrollbars; they shrink the visible area, the shadow shrinks back
 * inside, the scrollbars go, the shadow grows again — on every frame. Falling under a pixel short
 * of the edge doesn't show.
 */
function snapInside(length: number): number {
  const ratio = devicePixelRatio || 1;

  return Math.max(Math.floor((length - EDGE_LEEWAY) * ratio) / ratio, 0);
}

@Component({
  selector: 'fr8bot-ui-content-border-shadow',
  templateUrl: './content-border-shadow.component.html',
  // Sticky and zero-sized inside the shaded container: it stays put while the content scrolls,
  // takes no room, and the container's scrollbars still paint over the shadows.
  host: {
    class:
      'tw:pointer-events-none tw:sticky tw:top-0 tw:left-0 tw:z-8 tw:size-0 tw:flex-none tw:self-start',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentBorderShadowUIComponent {
  readonly sides = input.required<readonly ContentShadowSide[]>();
  /**
   * `true`: each shadow shows only as far as the content is scrolled away from its edge, so a
   * container that does not scroll has none. `false`: always at full strength (a map).
   */
  readonly followScroll = input(true);
  /** Opacity of a shadow at full strength; maps use a lighter 0.3. */
  readonly maxOpacity = input(DEFAULT_SHADOW_MAX_OPACITY);
  /**
   * An element pinned at the top of the container — a sticky header — that the top shadow starts
   * under instead of at the container's top edge. It should be a child of the container, so its
   * size changes are picked up.
   */
  readonly topInsetElement = input<HTMLElement | null>(null);

  protected readonly sideClass = SIDE_CLASS;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  constructor() {
    afterRenderEffect((onCleanup) => {
      const container = this.host.parentElement;
      const followScroll = this.followScroll();
      const maxOpacity = this.maxOpacity();
      const topInsetElement = this.topInsetElement();

      if (!container) {
        return;
      }

      // Written to the host's style right here rather than through bindings: a binding lands a
      // frame later, and for that frame a shadow sized to the container's previous, larger box
      // sticks out of it and flashes a scrollbar.
      const setVar = (name: string, value: string) => this.host.style.setProperty(name, value);

      const measure = () => {
        const horizontal = container.scrollWidth - container.clientWidth;
        const vertical = container.scrollHeight - container.clientHeight;
        const distances: Record<ContentShadowSide, number> = {
          top: container.scrollTop,
          left: container.scrollLeft,
          right: Math.max(horizontal - container.scrollLeft, 0),
          bottom: Math.max(vertical - container.scrollTop, 0),
        };

        // Normally the host already sits at the visible area's corner; this also covers a
        // container with padding, where it starts further in.
        const hostRect = this.host.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const { borderBox, visible } = exactSizes(container);
        // Rects are on screen, sizes in layout pixels: under a transform (a dialog still scaling in
        // when this first measures) the offsets come out shrunk, and the shadows, sized in layout
        // pixels, stick out of the container and give it a scrollbar. Against the rounded
        // `offsetWidth` a 551.349px column would read as scaled by 1.0006 and shift them out too.
        const scaleX = borderBox.width ? containerRect.width / borderBox.width : 1;
        const scaleY = borderBox.height ? containerRect.height / borderBox.height : 1;
        // The inset element's bottom padding is a spacer before the rows (the table header keeps
        // 2px there), so the top shadow starts right under its content instead.
        const topInset = topInsetElement
          ? topInsetElement.offsetHeight -
            parseFloat(getComputedStyle(topInsetElement).paddingBottom || '0')
          : 0;

        setVar(
          '--cbs-x',
          `${(containerRect.left - hostRect.left) / (scaleX || 1) + container.clientLeft}px`,
        );
        setVar(
          '--cbs-y',
          `${(containerRect.top - hostRect.top) / (scaleY || 1) + container.clientTop}px`,
        );
        setVar('--cbs-w', `${snapInside(visible.width)}px`);
        setVar('--cbs-h', `${snapInside(visible.height)}px`);
        setVar('--cbs-top-inset', `${topInset}px`);

        for (const side of SIDES) {
          const opacity = followScroll
            ? Math.min(distances[side] / FULL_STRENGTH_DISTANCE, 1) * maxOpacity
            : maxOpacity;

          setVar(`--cbs-opacity-${side}`, String(opacity));
        }
      };

      // The scroll range also changes when the content grows (rows loaded, columns resized)
      // while the container keeps its size, so its children are observed too.
      const resizeObserver = new ResizeObserver(measure);
      const observeChildren = () => {
        resizeObserver.disconnect();
        resizeObserver.observe(container);
        for (const child of Array.from(container.children)) {
          resizeObserver.observe(child);
        }
      };
      const mutationObserver = new MutationObserver(() => {
        observeChildren();
        measure();
      });

      observeChildren();
      mutationObserver.observe(container, { childList: true });
      container.addEventListener('scroll', measure, { passive: true });
      measure();

      onCleanup(() => {
        container.removeEventListener('scroll', measure);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      });
    });
  }
}
